import React, { useEffect, useState } from 'react';
import { attendanceAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { notify } from '../../utils/notify';
import { tn } from '../../utils/plural';
import Modal from '../../components/Modal';
import { addDays } from './weekModel';
import styles from './TimeAttendance.module.css';

/**
 * "Copy previous week": copies last week's days of the employees shown in
 * the grid onto this week. It first asks the server for a preview (how many
 * days would be created, how many skipped because they already have a
 * record, which land on a public holiday); nothing is ever overwritten.
 */
const CopyWeekDialog = ({ open, onClose, weekStart, employeeIds, onCopied }) => {
  const { t, locale } = useLanguage();
  const [includeAbsences, setIncludeAbsences] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const fromStart = addDays(weekStart, -7);
  const idsKey = employeeIds.join(',');

  useEffect(() => {
    // The ids as a string, so a new array with the same employees does not
    // ask for the preview again.
    const ids = idsKey ? idsKey.split(',').map(Number) : [];
    if (!open || ids.length === 0) {
      setPreview(null);
      return undefined;
    }
    let current = true;
    setLoading(true);
    attendanceAPI.copyWeek({
      from_start: fromStart, to_start: weekStart, include_absences: includeAbsences, employee_ids: ids, dry_run: true,
    })
      .then((res) => { if (current) setPreview(res.data); })
      .catch((error) => {
        if (current) {
          setPreview(null);
          notify(error.response?.data?.message || error.message, 'error');
        }
      })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [open, fromStart, weekStart, includeAbsences, idsKey]);

  const copy = async () => {
    setBusy(true);
    try {
      const res = await attendanceAPI.copyWeek({
        from_start: fromStart, to_start: weekStart, include_absences: includeAbsences, employee_ids: employeeIds,
      });
      notify([
        tn(t, res.data.created, '{count} day created', '{count} days created'),
        t('{count} skipped (they already had a record)', { count: res.data.skipped }),
      ].join(', '), 'success');
      onCopied();
    } catch (error) {
      notify(error.response?.data?.message || error.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const range = (start) => `${new Date(`${start}T00:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'short' })} – ${
    new Date(`${addDays(start, 6)}T00:00:00`).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })}`;
  const toCreate = preview ? preview.to_create : 0;

  return (
    <Modal
      open={open}
      title={t('Copy previous week')}
      onClose={onClose}
      busy={busy}
      footer={(
        <>
          <button type="button" className="btn btn--secondary" onClick={onClose} disabled={busy}>{t('Cancel')}</button>
          <button type="button" className="btn btn--primary" onClick={copy} disabled={busy || loading || toCreate === 0}>
            {tn(t, toCreate, 'Create {count} day', 'Create {count} days')}
          </button>
        </>
      )}
    >
      <p className={styles.dialogLead}>{t('From {from} into {to}.', { from: range(fromStart), to: range(weekStart) })}</p>
      <p className={styles.dialogWarning} role="note">
        {t('This creates real worked days. If someone does not come, change their day to absent or payroll will pay it.')}
      </p>
      <fieldset className={styles.dialogFieldset}>
        <legend>{t('What to copy')}</legend>
        <label className={styles.dialogCheck}>
          <input type="checkbox" checked disabled />
          <span>{t('Shifts (present days, with their clock-in and clock-out times)')}</span>
        </label>
        <label className={styles.dialogCheck}>
          <input type="checkbox" checked={includeAbsences} onChange={(e) => setIncludeAbsences(e.target.checked)} disabled={busy} />
          <span>{t('Absences, sick days and leave')}</span>
        </label>
      </fieldset>
      <p className={styles.muted}>
        {tn(t, employeeIds.length, 'Employees: the one shown in the grid.', 'Employees: the {count} shown in the grid.')}
      </p>

      {loading && <p className={styles.muted}>{t('Loading...')}</p>}
      {preview && !loading && (
        <div className={styles.copySummary} aria-live="polite">
          <div className={`${styles.copyStat} ${styles.copyCreate}`}>
            <strong>{preview.to_create}</strong>
            <span>{tn(t, preview.to_create, 'day will be created', 'days will be created')}</span>
          </div>
          <div className={styles.copyStat}>
            <strong>{preview.skipped}</strong>
            <span>{t('skipped: they already have a record or the employee is no longer active')}</span>
          </div>
          <div className={`${styles.copyStat} ${preview.on_holidays > 0 ? styles.copyHoliday : ''}`}>
            <strong>{preview.on_holidays}</strong>
            <span>
              {preview.holidays.length > 0
                ? t('on a public holiday: {names}', { names: preview.holidays.map((h) => h.name).join(', ') })
                : t('on a public holiday (none this week)')}
            </span>
          </div>
        </div>
      )}
      <p className={styles.muted}>
        {t('Nothing is overwritten. Days copied onto a public holiday count as worked holiday hours; change them in the grid if the post is closed. Lateness and overtime are not copied.')}
      </p>
    </Modal>
  );
};

export default CopyWeekDialog;
